import { saveAs } from 'file-saver'
import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import { Currency } from '@/features/offers/constants'

// UI
import { Box, Button, Flex, Heading, Spacer } from '@chakra-ui/react'

// Component

import { authSelector } from '@/stores/auth/authSlice'

import { TooltipCell } from '@/components/Table/components/Cell/ToolTipCell'

//API

//Constant
import { UserRole } from '@/features/users/constants'

//Helper
import { createExcelBlob } from '@/utils/fileHelper'
import { createICS } from '@/utils/outlook'

import { useInitialTableState } from '@/components/Table/components/useInitialTableState'
import FullTable from '@/components/Table/FullTable/FullTable'
import { convertTableStateToQuery } from '@/utils/tableHelper'
import { createColumnHelper } from '@tanstack/react-table'

import { ErrorSection } from '@/components/Elements'
import { RawTextCell } from '@/components/Table/components/Cell'
import DateCell from '@/components/Table/components/Cell/DateCell'
import { FilterType } from '@/components/Table/components/Filter/TableFilter'
import { isNullish } from '@/utils/helper'
import { formatCurrencyAmount } from '@/utils/stringHelper'
import useOfferList from '../hooks/useOfferList'

const OfferVendorRFQListPage = () => {
  const { user: currentUser } = useSelector(authSelector)
  // Local State
  const [storedTableState, setStoredTableState] = useInitialTableState(`table_state:offerVendorRFQ`, {})

  const query = convertTableStateToQuery(storedTableState)

  // Hooks
  const { useFetchOfferVendorRFQs, useFetchVendorRFQTenderDocOptions } = useOfferList()
  const { data: fetchOfferVendorRFQsResponse } = useFetchOfferVendorRFQs(query)
  const { data: tenderDocOptionsResponse } = useFetchVendorRFQTenderDocOptions()
  const { data: fetchAllOfferVendorRFQsResponse } = useFetchOfferVendorRFQs({
    page: 1,
    pageSize: 0,
    filters: query.filters,
  })

  const tenderDocFilterOptions = useMemo(() => {
    if (!tenderDocOptionsResponse?.data) return []
    return tenderDocOptionsResponse.data.map((idx) => ({
      label: idx === 0 ? 'Tender' : 'Addendum ' + idx,
      value: String(idx),
    }))
  }, [tenderDocOptionsResponse])

  const navigate = useNavigate()
  const columnHelper = createColumnHelper()

  const handleGotoOffer = (row) => {
    const { _id } = row.original

    if (isNullish(_id)) {
      return
    }
    navigate(`/offers/${_id}`)
  }

  const handleDownload = () => {
    if (!fetchOfferVendorRFQsResponse?.data) return
    try {
      const excelData = fetchAllOfferVendorRFQsResponse.data.map((VendorRFQs) => {
        // eslint-disable-next-line no-unused-vars
        const { customerObj, ...rest } = VendorRFQs
        return rest
      })
      const excelBlob = createExcelBlob(excelData)
      saveAs(excelBlob, 'Offer Vendor RFQs.xlsx')
    } catch (err) {
      console.log(err)
    }
  }
  const defaultColumns = useMemo(
    () => [
      columnHelper.display({
        id: 'action',
        size: 80,
        enableHiding: false,
        enableColumnFilter: false,
        cell: ({ row: { original } }) => {
          return (
            <Button
              size="sm"
              colorScheme="blue"
              onClick={(e) => {
                e.stopPropagation()

                createICS(original.dueDate, original.projectName, original._id, original.rfqNumber, false)
              }}
            >
              Outlook
            </Button>
          )
        },
      }),
      columnHelper.accessor('rfqNumber', {
        header: () => <span>RFQ #</span>,
        cell: RawTextCell,
      }),
      columnHelper.accessor('projectName', {
        cell: RawTextCell,
      }),
      columnHelper.accessor('subSystemName', {
        cell: RawTextCell,
      }),
      columnHelper.accessor('scopeOfSupply', {
        cell: RawTextCell,
      }),
      columnHelper.accessor('vendorName', {
        cell: RawTextCell,
      }),

      columnHelper.accessor('quoteAmount', {
        cell: ({ getValue, row: { original } }) => {
          const value = getValue()
          const tooltip = original?.lastUploadVendor ? `Last uploaded by ${original?.lastUploadVendor}` : ''
          return <TooltipCell text={formatCurrencyAmount(value ?? 0, original?.currency ?? '')} tooltip={tooltip} />
        },

        meta: {
          filterVariant: FilterType.RANGE,
        },
      }),
      columnHelper.accessor('dueDate', {
        cell: DateCell,
        meta: {
          filterVariant: FilterType.DATE,
        },
      }),
      columnHelper.accessor('validDate', {
        cell: DateCell,
        meta: {
          filterVariant: FilterType.DATE,
        },
      }),
      columnHelper.accessor('submitDate', {
        cell: DateCell,
        meta: {
          filterVariant: FilterType.DATE,
        },
      }),
      columnHelper.accessor('PM040SourcingSelected', {
        header: () => <span>PM040 Sourcing</span>,
        cell: ({ getValue }) => <TooltipCell text={getValue() === 'yes' ? 'Yes' : 'No'} />,
        meta: {
          filterVariant: FilterType.SELECT_YES_NO,
        },
      }),
      columnHelper.accessor('PM040TechnicalSelected', {
        header: () => <span>PM040 Technical</span>,
        cell: ({ getValue }) => <TooltipCell text={getValue() === 'yes' ? 'Yes' : 'No'} />,
        meta: {
          filterVariant: FilterType.SELECT_YES_NO,
        },
      }),
      columnHelper.accessor('PM100TechnicalSelected', {
        header: () => <span>PM100 Technical</span>,
        cell: ({ getValue }) => <TooltipCell text={getValue() === 'yes' ? 'Yes' : 'No'} />,
        meta: {
          filterVariant: FilterType.SELECT_YES_NO,
        },
      }),
      columnHelper.accessor('currency', {
        cell: RawTextCell,

        meta: {
          filterVariant: FilterType.MULTI_SELECT,
          filterOptions: Object.values(Currency).map((currency) => ({
            label: currency,
            value: currency,
          })),
        },
      }),
      columnHelper.accessor('procurementOfficerCommented', {
        header: () => <span>Procurement Commented</span>,
        cell: ({ getValue }) => <TooltipCell text={getValue() === 'yes' ? 'Yes' : 'No'} />,
        meta: {
          filterVariant: FilterType.SELECT_YES_NO,
        },
      }),
      columnHelper.accessor('uploader', {
        header: () => <span>Uploader</span>,
        cell: RawTextCell,
      }),
      columnHelper.accessor('tenderDocumentIndex', {
        header: () => <span>Tender Doc Version</span>,
        cell: ({ getValue }) => {
          const value = getValue()
          let text

          if (value === 0) {
            text = 'Tender'
          } else if (value === undefined || value === null) {
            text = 'Addendum 1'
          } else if (value > 1) {
            text = 'Addendum ' + value
          } else {
            text = 'Addendum ' + value
          }

          return <TooltipCell text={text} />
        },

        meta: {
          filterVariant: FilterType.MULTI_SELECT,
          filterOptions: tenderDocFilterOptions,
        },
      }),
      columnHelper.accessor('selection', {
        cell: ({ getValue }) => <TooltipCell text={getValue() === 'yes' ? 'Yes' : 'No'} />,
        meta: {
          filterVariant: FilterType.SELECT_YES_NO,
        },
      }),
      // eslint-disable-next-line react-hooks/exhaustive-deps
    ],
    [tenderDocFilterOptions]
  )

  if (!currentUser?.fullName) {
    return <>Loading...</>
  }

  if (currentUser.role === UserRole.VENDOR) {
    return <ErrorSection text="You don't have the permission to view this page"></ErrorSection>
  }
  return (
    <Box shadow="md" borderWidth="1px" bg="white" w="100%" mb="auto">
      <Flex p={5} borderBottom="1px solid #d8dbe0">
        <Heading>Vendor RFQs({fetchOfferVendorRFQsResponse?.count})</Heading>
        <Spacer />

        <Flex gap={1} alignItems={'center'}>
          <Button colorScheme={'green'} onClick={handleDownload}>
            Download
          </Button>
        </Flex>
      </Flex>
      <Box p={5} overflow={'scroll'}>
        <FullTable
          data={fetchOfferVendorRFQsResponse?.data ? fetchOfferVendorRFQsResponse.data : []}
          rowCount={fetchOfferVendorRFQsResponse?.count}
          columnDef={defaultColumns}
          state={storedTableState}
          setState={setStoredTableState}
          handleRowClick={handleGotoOffer}
        />
      </Box>
    </Box>
  )
}

export default OfferVendorRFQListPage
